"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.moveColumn = void 0;
const lodash_1 = require("lodash");
const ot_1 = require("../../engine/ot");
const model_1 = require("../../model");
const store_1 = require("../../exports/store");
const i18n_1 = require("../../exports/i18n");
const types_1 = require("../../types");
const command_manager_1 = require("../../command_manager");
exports.moveColumn = {
    undoable: true,
    execute: (context, options) => {
        const { model: state } = context;
        const { data, viewId } = options;
        const datasheetId = store_1.Selectors.getActiveDatasheetId(state);
        const snapshot = store_1.Selectors.getSnapshot(state, datasheetId);
        if (!snapshot) {
            return null;
        }
        const view = snapshot.meta.views.find(view => view.id === viewId);
        const getColumnIndexMap = () => {
            const columnsMap = {};
            if (!view) {
                return columnsMap;
            }
            for (const [k, v] of view.columns.entries()) {
                columnsMap[v.fieldId] = k;
            }
            return columnsMap;
        };
        const columnIndexMapById = getColumnIndexMap();
        if ((0, lodash_1.isEmpty)(data)) {
            return null;
        }
        if (!view) {
            throw new Error((0, i18n_1.t)(i18n_1.Strings.error_move_column_failed_invalid_params));
        }
        const frozenColumnCount = view.frozenColumnCount;
        let finalFrozenColumnCount = frozenColumnCount;
        const actions = data.reduce((collected, recordOption) => {
            const { fieldId, overTargetId, direction } = recordOption;
            const originColumnIndex = columnIndexMapById[fieldId];
            const targetColumnIndex = columnIndexMapById[overTargetId];
            let targetIndex = originColumnIndex > targetColumnIndex ? targetColumnIndex + 1 : targetColumnIndex;
            if (direction === store_1.DropDirectionType.BEFORE) {
                targetIndex--;
            }
            if (targetIndex === 0) {
                // Do not allow dragging other columns to the first column
                return collected;
            }
            if (originColumnIndex === 0) {
                // The first column does not allow dragging
                return collected;
            }
            const action = model_1.DatasheetActions.moveColumns2Action(snapshot, { fieldId, target: targetIndex, viewId });
            if (!action) {
                return collected;
            }
            if (frozenColumnCount) {
                if (targetIndex < frozenColumnCount && originColumnIndex >= frozenColumnCount) {
                    finalFrozenColumnCount++;
                }
                if (targetIndex >= frozenColumnCount && originColumnIndex < frozenColumnCount) {
                    finalFrozenColumnCount--;
                }
            }
            if (collected.length) {
                const transformedAction = ot_1.jot.transform([action], collected, 'right');
                collected.push(...transformedAction);
            }
            else {
                collected.push(action);
            }
            return collected;
        }, []);
        if (actions.length === 0) {
            return null;
        }
        if (frozenColumnCount && frozenColumnCount !== finalFrozenColumnCount) {
            const action = model_1.DatasheetActions.setFrozenColumnCount2Action(snapshot, { viewId, count: finalFrozenColumnCount });
            action && actions.push(action);
        }
        return {
            result: command_manager_1.ExecuteResult.Success,
            resourceId: datasheetId,
            resourceType: types_1.ResourceType.Datasheet,
            actions,
        };
    },
};
/*

 declare module '../../command_manager/command_manager' {
 interface CollaCommandManager {
 execute(options: IMoveRowOptions & { cmd: 'MoveRow' });
 }
 }

 */
//# sourceMappingURL=move_column.js.map